import { Flex } from "src/components/Flex.tsx";
import { IconButton } from "src/components/IconButton/IconButton.tsx";
import {
  Button,
  type ButtonSkin,
  type ButtonVariant,
} from "src/components/Button/Button.tsx";
import { CrossCircleIcon } from "src/lib/ui-icon-v2";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { Pagination } from "src/components/Pagination/Pagination.tsx";
import { useTranslation } from "react-i18next";
import { useGridSelector } from "@mui/x-data-grid";
import {
  gridFilterModelSelector as realGridFilterModelSelector,
  gridPageCountSelector as realGridPageCountSelector,
  type GridPaginationModel,
  gridPaginationModelSelector as realGridPaginationModelSelector,
  gridPaginationRowCountSelector as realGridPaginationRowCountSelector,
} from "@mui/x-data-grid-premium";
import React, {
  Fragment,
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
} from "react";
import type { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { ThemeProvider } from "@mui/material";
import { Scrollable } from "src/components/Scrollable/Scrollable.tsx";
import * as styles from "./DataGridToolbar.style.ts";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { observer } from "mobx-react-lite";

export type ToolbarProps = {
  hideCreateNewRecordButton?: boolean;
  hideCsvButton?: boolean;
  filterMode?: "server" | "client";
  onExportCsv?: (e: MouseEvent<HTMLButtonElement>) => void;
  csvExportPending?: boolean;
  onCreateNewRecord?: (e: MouseEvent<HTMLButtonElement>) => void;
  hidePagination?: boolean;
  hideRefreshButton?: boolean;
  disableRefreshButton?: boolean;
  onRefresh?: (e: MouseEvent<HTMLButtonElement>) => void;
  startAdditionalElements?: ReactNode[];
  endAdditionalElements?: ReactNode[];
  disablePagination?: boolean;
  disableCreateNewRecordButton?: boolean;
  createNewRecordText?: string;
  createNewRecordType?: HTMLButtonElement["type"];
  createNewRecordSkin?: ButtonSkin;
  createNewRecordVariant?: ButtonVariant;
  apiRef: MutableRefObject<GridApiPremium>;
};

const dumpGridPageCountSelector = () => 0;
const dumpGridPaginationModelSelector = () => ({
  page: 0,
  pageSize: 0,
});

const dumpGridFilterModelSelector = () => ({
  items: [],
});

const dumpGridPaginationRowCountSelector = () => 0;

export const DataGridToolbar = observer(
  ({
    onRefresh,
    apiRef,
    hideCsvButton,
    onCreateNewRecord,
    hideCreateNewRecordButton,
    disableCreateNewRecordButton,
    disableRefreshButton,
    onExportCsv,
    csvExportPending = false,
    createNewRecordText,
    filterMode,
    createNewRecordSkin = "success",
    createNewRecordVariant = "filled",
    createNewRecordType,
    startAdditionalElements,
    endAdditionalElements,
    disablePagination,
    hidePagination,
    hideRefreshButton,
  }: ToolbarProps) => {
    const apiRefInitialized = !!apiRef.current.instanceId;

    const gridPageCountSelector = apiRefInitialized
      ? realGridPageCountSelector
      : dumpGridPageCountSelector;
    const gridPaginationModelSelector = apiRefInitialized
      ? realGridPaginationModelSelector
      : dumpGridPaginationModelSelector;
    const gridPaginationRowCountSelector = apiRefInitialized
      ? realGridPaginationRowCountSelector
      : dumpGridPaginationRowCountSelector;

    const gridFilterModelSelector = apiRefInitialized
      ? realGridFilterModelSelector
      : dumpGridFilterModelSelector;

    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

    const paginationModel: GridPaginationModel = useGridSelector(
      apiRef,
      gridPaginationModelSelector,
    );

    const store = useDataGridStore();

    // row count can be undefined
    const unsafeRowCount = useGridSelector(
      apiRef,
      gridPaginationRowCountSelector,
    ) as number | undefined;
    // idea: should we hide pagination until row count is defined?
    const rowCount = unsafeRowCount ?? 0;
    const lastPage = React.useMemo(
      () => Math.max(0, pageCount - 1),
      [pageCount],
    );

    const computedPage = React.useMemo(() => {
      if (rowCount === -1) {
        return paginationModel.page;
      }
      return paginationModel.page <= lastPage ? paginationModel.page : lastPage;
    }, [lastPage, paginationModel.page, rowCount]);

    const handlePageChange = React.useCallback(
      (page: number) => {
        apiRef.current.setPage(page);
      },
      [apiRef],
    );

    const fromPage = Math.min(
      computedPage * paginationModel.pageSize + 1,
      rowCount,
    );
    const toPage = Math.min(
      (computedPage + 1) * paginationModel.pageSize,
      rowCount,
    );

    const { t } = useTranslation();
    const theme = useNonConflictingTheme();

    // const toggleFilterPanel = () => {
    //   if (apiRef.current.state.preferencePanel.open) {
    //     apiRef.current.hideFilterPanel();
    //   } else {
    //     apiRef.current.showFilterPanel();
    //   }
    // };

    const resetFilters = () => {
      apiRef.current.setFilterModel({
        items: [],
      });
      store.setAppliedFilterModel({
        items: [],
      });
    };

    const filterCount =
      filterMode === "server"
        ? store.appliedFilterModel.items.length
        : filterModel.items.length;

    const hasAppliedFilters = filterCount > 0;

    return (
      <ThemeProvider theme={theme}>
        <Scrollable disableVertical css={styles.root}>
          {/*
            Строка переносится, а не прячет правую группу за краем окна.
            Раньше при широком дополнительном элементе слева (полоса состояний
            возвратов — 863px) пагинация уезжала за правый край, и на списке в
            сорок тысяч строк перелистывать было нечем: собственный скроллер
            тулбара полосы прокрутки не показывает. Перенос ничего не прячет и
            не даёт группам наехать друг на друга.
          */}
          <Flex
            justifyContent="space-between"
            gap="10px"
            flexShrink={0}
            flexWrap="wrap"
            css={styles.inner}
          >
            {/*
              Группа переносит своё содержимое и ужимается: широкий
              дополнительный элемент (полоса состояний возвратов — 863px) на
              узком окне иначе уезжает за край вместе с последними состояниями.
              Кнопки при переносе остаются на своей строке в полную ширину —
              автоминимум по самому широкому элементу не даёт им сжаться.
            */}
            <Flex gap="10px" alignItems="center" flexShrink={1} flexWrap="wrap">
              <IconButton
                variant="outlined"
                size="sm"
                style={{
                  display: hideRefreshButton ? "none" : undefined,
                }}
                skin="info"
                onClick={onRefresh}
                disabled={disableRefreshButton}
              >
                <i className="icon-15-update" />
              </IconButton>

              <Button
                size="sm"
                css={styles.createNewRecordButton}
                style={{
                  display: hideCreateNewRecordButton ? "none" : undefined,
                }}
                startAdornment={
                  <CrossCircleIcon css={styles.createNewRecordButtonIcon} />
                }
                disabled={disableCreateNewRecordButton}
                variant={createNewRecordVariant}
                skin={createNewRecordSkin}
                type={createNewRecordType}
                onClick={onCreateNewRecord}
              >
                {createNewRecordText ?? "Create new record"}
              </Button>

              <Button
                size="sm"
                variant="outlined"
                style={{
                  // Без обработчика кнопке нечего делать: грид не из списка ресурса.
                  display: hideCsvButton || !onExportCsv ? "none" : undefined,
                }}
                isPending={csvExportPending}
                disabled={csvExportPending}
                onClick={onExportCsv}
                startAdornment={<span className={"icon-15-import"} />}
              >
                {t("csv", { ns: TranslationNamespaceEnum.Common })}
              </Button>

              {startAdditionalElements?.map((el, index) => {
                return <Fragment key={index}>{el}</Fragment>;
              })}
              {/*{filterModel.items.length > 0 && (*/}
              {/*  <ButtonV2*/}
              {/*    onClick={toggleFilterPanel}*/}
              {/*    variant={"outlined"}*/}
              {/*    skin={"info"}*/}
              {/*    style={{*/}
              {/*      flexShrink: 0,*/}
              {/*    }}*/}
              {/*    count={filterModel.items.length}*/}
              {/*    startAdornment={<span className={"icon-15-filter"} />}*/}
              {/*    size={"sm"}*/}
              {/*  >*/}
              {/*    {t("filter", {*/}
              {/*      ns: TranslationNamespaceEnum.DataGridComponent,*/}
              {/*    })}*/}
              {/*  </ButtonV2>*/}
              {/*)}*/}

              <Button
                onClick={resetFilters}
                variant={"outlined"}
                skin={"danger"}
                style={{
                  flexShrink: 0,
                  display: !hasAppliedFilters ? "none" : undefined,
                }}
                count={filterCount}
                startAdornment={<span className={"icon-15-filter"} />}
                size={"sm"}
              >
                {t("resetFilters", {
                  ns: TranslationNamespaceEnum.DataGridComponent,
                })}
              </Button>
            </Flex>
            <Flex gap={"10px"} alignItems="center" flexShrink={0}>
              {endAdditionalElements?.map((el, index) => {
                return <Fragment key={index}>{el}</Fragment>;
              })}
              {!hidePagination && (
                <Pagination
                  showing={{
                    from: fromPage,
                    to: toPage,
                    total: rowCount,
                  }}
                  disabled={disablePagination}
                  count={pageCount}
                  page={Math.min(computedPage + 1, pageCount)}
                  onChange={(page) => handlePageChange(page - 1)}
                />
              )}
            </Flex>
          </Flex>
        </Scrollable>
      </ThemeProvider>
    );
  },
);
